// ChatPanel.tsx

import {
  Textarea,
  Button,
  Box,
  Text,
  Flex,
  VStack,
  useToast,
  Progress,
  Spinner,
  HStack,
  Icon,
  Badge,
  CircularProgress,
  CircularProgressLabel,
} from "@chakra-ui/react";
import {
  useState,
  KeyboardEvent,
  useEffect,
  useRef,
  ReactElement,
  useCallback,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../services/state/store";
import SockJS from "sockjs-client";
import { over } from "stompjs";
import { useApplications } from "../../pages/Applications/useApplications";
import { BrandButton } from "../BrandButton/BrandButton";
import { BsStars } from "react-icons/bs";
import {
  AddAgentInputData,
  AgentMetadata,
  GENERATION_MILESTONES,
} from "../../services/state/chat/chat-interfaces";
import {
  addAgent,
  generateApplication,
  importApplication,
} from "../../services/state/chat/chat-async-calls";
import {
  addMessage,
  setLoading,
  updateLastMessage,
  updateMilestoneProgress,
} from "../../services/state/chat/chat";
import { useNavigate } from "react-router-dom";
import { UserService } from "../../services/UserService";

// ... (Interfaces and ProgressMessage component are correct and remain the same)
interface MessageResponse {
  messageUid: string;
  processCd: string;
  responseBody: {
    responseMsg: string;
    messageType: string;
    messageOrder: number;
    activityType: string;
    activityCode: string;
    audioUrl?: string;
    requestFor?: string;
    contextMsg?: string;
    additionalMsg?: string;
    voiceId?: string;
    voiceEngine?: string;
    metaData?: any;
    suggestedQuestions?: string[];
  };
  headers: {
    validatorActivityCode?: string;
    validatorParameter?: string;
    question?: string;
    questionActivityCode?: string;
  };
}

// Progress Message Component
const ProgressMessage = ({
  message,
  progress = 0,
  currentStep = "",
  milestoneStep = 0,
}: {
  message: string;
  progress?: number;
  currentStep?: string;
  milestoneStep?: number;
}) => {
  return (
    <Box
      bg="rgba(118, 82, 247, 0.2)"
      backdropFilter="blur(10px)"
      p={4}
      borderRadius="lg"
      border="1px"
      borderColor="rgba(118, 82, 247, 0.3)"
    >
      <HStack justify="space-between" mb={3}>
        <Text fontWeight="medium" color="brand.600">
          {message}
        </Text>
      </HStack>

      <Progress
        value={progress}
        colorScheme="brand"
        size="lg"
        borderRadius="full"
        mb={2}
      />

      <Flex justify="space-between" align="center">
        <Text fontSize="sm" color="brand.600">
          {currentStep}
        </Text>
        <Text fontSize="sm" fontWeight="bold" color="brand.600">
          {progress}%
        </Text>
      </Flex>
    </Box>
  );
};

export const ChatPanel = ({
  topSpacing = 18,
}: { topSpacing?: number } = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const toast = useToast();

  const {
    messages: chatMessages,
    loading,
    currentRequestId,
    generationProgress,
    currentStep,
    currentMilestone,
    totalMilestones,
  } = useSelector((state: RootState) => state.chat);

  // Local state
  const [message, setMessage] = useState("");
  const [mentionQuery, setMentionQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedApps, setSelectedApps] = useState<any[]>([]);

  // WebSocket related state
  const [client, setClient] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>("");
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);

  // State to track if we're waiting for generation to complete via WebSocket
  const [isGenerating, setIsGenerating] = useState(false);

  const chatBodyRef = useRef<HTMLDivElement>(null);
  const clientRef = useRef<any>(null);
  // Ref to track if generation completion has been processed to avoid duplicates
  const generationCompletedRef = useRef<boolean>(false);
  const { applications } = useApplications();

  // Generate session ID
  useEffect(() => {
    const newSessionId = `session_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    setSessionId(newSessionId);
  }, []);

  // Handler for when generation completes via WebSocket
  const handleGenerationComplete = useCallback(
    async (zipUrl: string, metadata: AgentMetadata) => {
      // Prevent duplicate processing
      if (generationCompletedRef.current) {
        console.log("Generation already completed, skipping duplicate");
        return;
      }
      generationCompletedRef.current = true;

      console.log("Processing GENERATION_COMPLETE:", { zipUrl, metadata });

      dispatch(
        updateLastMessage({
          text: "✅ Application generated! Importing now...",
          progress: 100,
          isLoading: false,
        })
      );

      try {
        const importResult = await dispatch(importApplication(zipUrl));

        if (importApplication.fulfilled.match(importResult)) {
          dispatch(
            addMessage({
              text: "📦 Application imported successfully!",
              isUser: false,
            })
          );

          if (metadata && metadata.agent) {
            dispatch(
              addMessage({
                text: `🤖 Would you like me to create an agent "${metadata.agent.agentName}" for this application?`,
                isUser: false,
                showAgentConfirmation: true,
                agentMetadata: metadata,
              })
            );

            if (metadata.metadata.applicationId) {
              setTimeout(() => {
                navigate(
                  `/platform/design/objects/${metadata.metadata.applicationId}`
                );
              }, 1500);
            }
          } else {
            dispatch(
              addMessage({
                text: "All done! Your application is ready.",
                isUser: false,
              })
            );
            dispatch(setLoading(false));
          }
        } else {
          dispatch(
            addMessage({
              text: "⚠️ App generated but failed to import. Please try manually.",
              isUser: false,
            })
          );
          dispatch(setLoading(false));
        }
      } catch (error) {
        console.error("Import error:", error);
        dispatch(
          addMessage({
            text: "❌ Failed to import application. Please try again.",
            isUser: false,
          })
        );
        dispatch(setLoading(false));
      } finally {
        setIsGenerating(false);
      }
    },
    [dispatch, navigate]
  );

  // WebSocket connection setup
  useEffect(() => {
    let reconnectInterval: NodeJS.Timeout | null = null;

    const connect = () => {
      if (!sessionId) return;

      const endpointUrl = process.env.REACT_APP_WEBSOCKET_BACKEND_API;
      const socketUrl = `${endpointUrl}/web-socket?access_token=45a1e149-ba2c-45ec-8ea5-d0a702fad2a1`;
      const socket = new SockJS(socketUrl);
      const stompClient = over(socket);

      stompClient.connect(
        {},
        (frame) => {
          console.log("WebSocket Connected: " + frame);
          setIsWebSocketConnected(true);

          // Subscribe to main output channel
          stompClient.subscribe(`/main/output/${sessionId}`, (message) => {
            try {
              const parsedMessage: MessageResponse = JSON.parse(message.body);
              const responseBody = parsedMessage.responseBody;

              console.log(
                "WebSocket message received:",
                parsedMessage
              );

              // Handle GENERATION_COMPLETE message type - this is the main completion signal
              if (responseBody.messageType === "GENERATION_COMPLETE") {
                console.log("GENERATION_COMPLETE received!");

                // Parse the responseMsg which contains the actual response data
                try {
                  const responseData = JSON.parse(responseBody.responseMsg);
                  console.log("Parsed GENERATION_COMPLETE data:", responseData);

                  if (responseData.success && responseData.zip_url && responseData.metadata) {
                    // Extract metadata - it may be nested
                    const metadata: AgentMetadata = responseData.metadata;
                    const zipUrl: string = responseData.zip_url;

                    // Call the handler to process the completion
                    handleGenerationComplete(zipUrl, metadata);
                    return;
                  } else {
                    console.error("GENERATION_COMPLETE missing required data:", responseData);
                    dispatch(
                      addMessage({
                        text: "❌ Generation completed but response was invalid.",
                        isUser: false,
                      })
                    );
                    dispatch(setLoading(false));
                    setIsGenerating(false);
                  }
                } catch (parseError) {
                  console.error("Error parsing GENERATION_COMPLETE responseMsg:", parseError);
                }
                return;
              }

              // Update milestone progress based on received message FIRST
              dispatch(
                updateMilestoneProgress({
                  message: responseBody.responseMsg,
                })
              );

              // Only add non-milestone messages to avoid duplicates
              const isProgressMessage = responseBody.responseMsg.match(
                /^\d+\.\s*(📝|🎯|🖥|🔌|🔍|📦|🚀)/
              );
              if (!isProgressMessage) {
                dispatch(
                  addMessage({
                    text: responseBody.responseMsg,
                    isUser: false,
                  })
                );
              }

              // Handle different message types
              if (responseBody.messageType === "COMPLETION") {
                dispatch(setLoading(false));
                // Mark progress as complete
                dispatch(
                  updateMilestoneProgress({
                    message: "Generation completed",
                    step: GENERATION_MILESTONES.length,
                    progress: 100,
                  })
                );
              }

              if (responseBody.messageType === "ERROR") {
                dispatch(setLoading(false));
                setIsGenerating(false);
                dispatch(
                  addMessage({
                    text: `❌ Error: ${responseBody.responseMsg}`,
                    isUser: false,
                  })
                );
              }
            } catch (error) {
              console.error("Error parsing WebSocket message:", error);
            }
          });

          // Subscribe to frame output
          stompClient.subscribe(
            `/main/frame/output/${sessionId}`,
            (message) => {
              try {
                const parsedMessage: MessageResponse = JSON.parse(message.body);
                const responseBody = parsedMessage.responseBody;

                // Update milestone progress for frame messages too
                dispatch(
                  updateMilestoneProgress({
                    message: responseBody.responseMsg,
                  })
                );

                // Only add frame messages that aren't already milestone messages
                const isProgressMessage = responseBody.responseMsg.match(
                  /^\d+\.\s*(📝|🎯|🖥|🔌|🔍|📦|🚀)/
                );
                if (!isProgressMessage) {
                  dispatch(
                    addMessage({
                      text: `🔄 ${responseBody.responseMsg}`,
                      isUser: false,
                    })
                  );
                }
              } catch (error) {
                console.error("Error parsing frame message:", error);
              }
            }
          );

          setClient(stompClient);
          clientRef.current = stompClient;

          if (reconnectInterval) {
            clearInterval(reconnectInterval);
            reconnectInterval = null;
          }
        },
        (error) => {
          console.log("WebSocket Connection error:", error);
          setIsWebSocketConnected(false);

          if (!reconnectInterval) {
            reconnectInterval = setInterval(connect, 5000);
          }
        }
      );
    };

    if (sessionId) {
      connect();
    }

    return () => {
      if (client) {
        client.disconnect(() => {
          console.log("WebSocket Disconnected");
        });
      }
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }
    };
  }, [sessionId, dispatch, handleGenerationComplete]);

  // Auto-scroll
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!message.trim() || loading) return;

    const userInput = message.trim();
    const requestId = Date.now().toString();

    // Reset the generation completed flag for new generation
    generationCompletedRef.current = false;
    setIsGenerating(true);

    dispatch(addMessage({ text: userInput, isUser: true }));
    dispatch(
      addMessage({
        text: "🚀 Starting application generation...",
        isUser: false,
        isLoading: true,
        progress: 0,
        milestoneStep: 0,
      })
    );
    setMessage("");

    try {
      // Fire the API request but don't wait for it to complete
      // The WebSocket will receive the GENERATION_COMPLETE message with the result
      dispatch(
        generateApplication({
          userInput,
          selectedApps: selectedApps.map((app) => ({
            uuid: app.uuid,
            name: app.appName,
          })),
          requestId,
          sessionId,
        })
      )
        .then((resultAction) => {
          // This callback runs when API returns, but we've already processed via WebSocket
          // Only handle errors here if WebSocket didn't already process completion
          if (
            !generationCompletedRef.current &&
            generateApplication.rejected.match(resultAction)
          ) {
            const error =
              (resultAction.payload as string) || "An unknown error occurred.";
            dispatch(
              updateLastMessage({
                text: `❌ Oops! Something went wrong. ${error}`,
                isLoading: false,
              })
            );
            dispatch(setLoading(false));
            setIsGenerating(false);
          }
        })
        .catch((error) => {
          // Handle network errors only if WebSocket didn't already complete
          if (!generationCompletedRef.current) {
            console.error("Generation error:", error);
            dispatch(setLoading(false));
            setIsGenerating(false);
            dispatch(
              addMessage({
                text: "❌ Failed to generate application. Please try again.",
                isUser: false,
              })
            );
          }
        });

      // Don't await - let WebSocket handle the completion via GENERATION_COMPLETE message
      console.log("Generation request sent, waiting for WebSocket GENERATION_COMPLETE...");
    } catch (error) {
      console.error("Generation request error:", error);
      dispatch(setLoading(false));
      setIsGenerating(false);
      dispatch(
        addMessage({
          text: "❌ Failed to start application generation. Please try again.",
          isUser: false,
        })
      );
    }
  };

  const handleAgentConfirmation = async (
    shouldCreate: boolean,
    agentMetadata: AgentMetadata
  ) => {
    dispatch(
      updateLastMessage({ showAgentConfirmation: false, text: "Got it!" })
    );

    if (shouldCreate) {
      dispatch(addMessage({ text: "🤖 Creating agent...", isUser: false }));
      const inputData: AddAgentInputData = {
        processCd: agentMetadata.agent.processCd,
        processName: agentMetadata.agent.processName,
        agentId: agentMetadata.agent.agentId,
        agentName: agentMetadata.agent.agentName,
        serviceCd: "EIZEN_GNRL_SVC",
        serviceType: "private",
        sourceType: agentMetadata.vip.sourceType,
        models: agentMetadata.vip.models.map((model) => model.id),
        // models: [124],
        firstName: "Demo",
        lastName: "User",
        userName: UserService.getUserEmail(),
        password: "1234",
        zoneName: agentMetadata.agent.serviceName,
        email: UserService.getUserEmail(),
        phoneNum: "N/A",
        sourceUserName: "defaultUser",
        sourcePassword: "defaultPassword",
        querynodeCmsExampleQuestions:
          agentMetadata.node_prompts.querynode_cms_example_questions,
        querynodeCmsInstructions:
          agentMetadata.node_prompts.querynode_cms_instructions,
        querynodeEventExampleQuestions:
          agentMetadata.node_prompts.querynode_event_example_questions,
        querynodeEventInstructions:
          agentMetadata.node_prompts.querynode_event_instructions,
        processSummary: agentMetadata.node_prompts.process_summary,
        eventVideos: agentMetadata.node_prompts.event_videos,
        sourceUrl:
          "rtsp://admin:admin123456@192.168.0.54:554/cam/realmonitor?channel=1&subtype=0",
      };
      const result = await dispatch(addAgent(inputData));

      if (addAgent.fulfilled.match(result)) {
        dispatch(
          addMessage({
            text: `🚀 Agent "${agentMetadata.agent.agentName}" created successfully!`,
            isUser: false,
          })
        );
        dispatch(
          addMessage({
            text: `Visit <a href="${process.env.REACT_APP_AGENT_UI_URL}" target="_blank">Agent Dashboard</a> to view and manage it.`,
            isUser: false,
          })
        );
      } else {
        dispatch(
          addMessage({
            text: "⚠️ Failed to create agent. You can do it manually later.",
            isUser: false,
          })
        );
      }
    } else {
      dispatch(
        addMessage({
          text: "✅ You can create an agent later if needed.",
          isUser: false,
        })
      );
    }
    dispatch(setLoading(false));
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !loading) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Box w="100%">
      {/* Minimal spacer to align with content */}
      <Box h={`${topSpacing}px`} />

      {/* Main Chat Panel */}
      <Box
        bg="rgba(255, 255, 255, 0.05)"
        backdropFilter="blur(20px)"
        borderRadius="20px"
        borderWidth="1px"
        borderColor="rgba(255, 255, 255, 0.1)"
        w="100%"
        h={"85vh"}

        // h={`calc(90vh - ${topSpacing}px)`}
      >
        <Flex
          direction="column"
          h="100%"
          w="100%"
          bg="transparent"
          borderRadius="20px"
          overflow="hidden"
        >
          {/* Panel Header - matching Your Applications header */}
          <Box p={6} bg="transparent">
            <Flex align="center" gap={2} mb={0}>
              <Icon
                as={BsStars as React.ElementType}
                boxSize={6}
                color="brand.500"
              />
              <Text fontSize="18px" fontWeight="600" color="white">
                AI Assistant
              </Text>
              <Box
                w={3}
                h={3}
                borderRadius="full"
                bg={isWebSocketConnected ? "green.400" : "red.400"}
                title={isWebSocketConnected ? "Connected" : "Disconnected"}
                ml="auto"
              />
              {loading && (
                <HStack spacing={2}>
                  <Spinner size="sm" color="brand.500" />
                </HStack>
              )}
            </Flex>
          </Box>

          {/* Separator line */}
          <Box borderBottom="1px" borderColor="rgba(255, 255, 255, 0.1)" />

          {/* Chat Body */}
          <VStack
            ref={chatBodyRef}
            flex="1"
            overflowY="auto"
            spacing={4}
            px={4}
            py={2}
            bg="transparent"
          >
            {chatMessages.length === 0 ? (
              <VStack
                spacing={4}
                mt={8}
                textAlign="center"
                justify="center"
                h="100%"
              >
                <Text color="white" fontSize="lg" fontWeight="600">
                  👋 Hi there! I'm your AI Assistant
                </Text>
                <Text color="rgba(255, 255, 255, 0.7)" fontSize="sm">
                  I can help you generate applications instantly with real-time
                  milestone tracking.
                </Text>
                <Text color="brand.400" fontSize="sm" fontWeight="medium">
                  • "Create an app for project management"
                </Text>
                <Text
                  color={isWebSocketConnected ? "green.500" : "red.500"}
                  fontSize="xs"
                >
                  {isWebSocketConnected
                    ? "🟢 Real-time updates enabled"
                    : "🔴 Connecting..."}
                </Text>
              </VStack>
            ) : (
              <>
                {chatMessages
                  .filter((msg) => !msg.isLoading)
                  .map((msg) => (
                    <Flex
                      key={msg.id}
                      w="100%"
                      justify={msg.isUser ? "flex-end" : "flex-start"}
                    >
                      <Box maxW="90%">
                        <Box
                          bg={
                            msg.isUser
                              ? "brand.700"
                              : "rgba(255, 255, 255, 0.1)"
                          }
                          color="white"
                          p={4}
                          borderRadius="20px"
                          boxShadow="0 4px 15px rgba(0, 0, 0, 0.2)"
                          backdropFilter="blur(10px)"
                          border="1px solid"
                          borderColor={
                            msg.isUser
                              ? "brand.500"
                              : "rgba(255, 255, 255, 0.2)"
                          }
                        >
                          <Text
                            fontSize="sm"
                            whiteSpace="pre-wrap"
                            dangerouslySetInnerHTML={{
                              __html: msg.text.replace(
                                /<a href="([^"]*)" target="_blank">([^<]*)<\/a>/g,
                                '<a href="$1" target="_blank" style="color: #B5A0FF; text-decoration: underline; cursor: pointer;">$2</a>'
                              ),
                            }}
                          />
                          {msg.showAgentConfirmation && msg.agentMetadata && (
                            <HStack spacing={2} mt={3}>
                              <Button
                                size="sm"
                                colorScheme="brand"
                                onClick={() =>
                                  handleAgentConfirmation(
                                    true,
                                    msg.agentMetadata!
                                  )
                                }
                              >
                                Yes, Create
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  handleAgentConfirmation(
                                    false,
                                    msg.agentMetadata!
                                  )
                                }
                              >
                                Skip
                              </Button>
                            </HStack>
                          )}
                        </Box>
                        <Text
                          fontSize="xs"
                          color="gray.500"
                          mt={1}
                          textAlign={msg.isUser ? "right" : "left"}
                        >
                          {msg.timestamp}
                        </Text>
                      </Box>
                    </Flex>
                  ))}

                {loading && chatMessages.some((msg) => msg.isLoading) && (
                  <Box w="100%">
                    {(() => {
                      const loadingMsg = chatMessages.find(
                        (msg) => msg.isLoading
                      );
                      return loadingMsg ? (
                        <ProgressMessage
                          message={loadingMsg.text}
                          progress={loadingMsg.progress || generationProgress}
                          currentStep={loadingMsg.currentStep || currentStep}
                          milestoneStep={
                            loadingMsg.milestoneStep || currentMilestone
                          }
                        />
                      ) : null;
                    })()}
                  </Box>
                )}
              </>
            )}
          </VStack>

          {/* Input Area */}
          <Box p={4} bg="transparent" position="relative">
            <Box
              bg="rgba(255, 255, 255, 0.05)"
              backdropFilter="blur(20px)"
              borderRadius="20px"
              border="1px solid rgba(255, 255, 255, 0.2)"
              p={4}
              _hover={{
                borderColor: "rgba(255, 255, 255, 0.3)",
              }}
              _focusWithin={{
                borderColor: "brand.500",
                boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.3)",
              }}
              transition="all 0.2s ease"
            >
              <Textarea
                placeholder={
                  loading
                    ? "Generating your application..."
                    : "Describe the app you want to create..."
                }
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                isDisabled={loading}
                size="sm"
                rows={3}
                bg="transparent"
                border="none"
                color="white"
                resize="none"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _focus={{
                  border: "none",
                  boxShadow: "none",
                }}
                _hover={{
                  border: "none",
                }}
              />
              <Flex justify="flex-end" width="100%" mt={3}>
                <BrandButton
                  onClick={handleSendMessage}
                  isDisabled={!message.trim() || loading}
                  isLoading={loading}
                  // size="sm"
                  // minW="80px"
                >
                  {loading ? `Generating... (${generationProgress}%)` : "Send"}
                </BrandButton>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};
